import { apiUrl, apiFetch } from '../../config/api';

export async function fetchPlaces(headers) {
  const res = await apiFetch(apiUrl('/places?limit=50'), { headers });
  const data = await res.json();
  return data.success ? (data.data || []) : null;
}

export async function fetchPlaceLedger(headers, placeId) {
  const res = await apiFetch(apiUrl(`/places/${placeId}/ledger-settings`), { headers });
  const data = await res.json();
  return data.success ? (data.data || null) : null;
}

export async function addPlaceLedgerBalance(headers, placeId, amount) {
  const res = await apiFetch(apiUrl(`/places/${placeId}/ledger-balance`), {
    method: 'POST',
    headers,
    body: JSON.stringify({ amount }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.message || 'Failed to add balance');
    err.code = data?.code;
    throw err;
  }
  return data.data || null;
}

export async function fetchCategories(headers) {
  const res = await apiFetch(apiUrl('/categories/admin?limit=50'), { headers });
  const data = await res.json();
  return data.success ? (data.data || []) : null;
}

export async function fetchTags(headers) {
  const res = await apiFetch(apiUrl('/tags/admin?limit=100'), { headers });
  const data = await res.json();
  return data.success ? (data.data || []) : null;
}

export async function fetchBanners(headers) {
  const res = await apiFetch(apiUrl('/features/admin?limit=50'), { headers });
  const data = await res.json();
  return data.success ? (data.data || []) : null;
}

export async function fetchFeaturedItems(headers) {
  const res = await apiFetch(apiUrl('/featured-items'), { headers });
  const data = await res.json();
  return data.success ? (data.data?.featuredItems || []) : null;
}

export async function fetchUsers(headers) {
  const res = await apiFetch(apiUrl('/users?limit=50'), { headers });
  const data = await res.json();
  return data.success ? (data.data || []) : null;
}

const BUSINESS_STATUS_ORDER = {
  "didn't_contact_yet": 0,
  successfully_contacted: 1,
  "didn't_contact_successfully": 2,
  "didn't_meet_criteria": 3,
  rejected_offer: 4,
};

function sortBusinessSubmissions(submissions) {
  const arr = [...(submissions || [])];
  arr.sort((a, b) => {
    const orderA = BUSINESS_STATUS_ORDER[a.status] ?? 999;
    const orderB = BUSINESS_STATUS_ORDER[b.status] ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    return new Date(b.submittedAt || b.createdAt) - new Date(a.submittedAt || a.createdAt);
  });
  return arr;
}

export async function fetchBusinessSubmissions(headers) {
  const res = await apiFetch(apiUrl('/contact/business/admin?limit=50'), { headers });
  const data = await res.json();
  if (!data.success) return null;
  return sortBusinessSubmissions(data.data || []);
}

export async function fetchRecentBusinessSubmissions(headers) {
  const res = await apiFetch(apiUrl("/contact/business/admin?status=didn't_contact_yet&limit=5"), { headers });
  const data = await res.json();
  if (!data.success) return null;
  return sortBusinessSubmissions(data.data || []);
}

export async function fetchTopicsAdmin(headers) {
  const res = await apiFetch(apiUrl('/topics/admin/all'), { headers });
  const data = await res.json();
  return data.success ? (data.data?.topics || []) : null;
}

export async function fetchRedeemedOffersAnalytics(headers) {
  const res = await apiFetch(apiUrl('/analytics/redeemed-offers'), { headers });
  const data = await res.json();
  return data.success ? data.data : null;
}

export async function fetchAnalytics(headers) {
  const [overviewRes, appEventsRes, redeemedRes] = await Promise.all([
    apiFetch(apiUrl('/analytics/overview'), { headers }),
    apiFetch(apiUrl('/analytics/app-events'), { headers }),
    apiFetch(apiUrl('/analytics/redeemed-offers'), { headers }),
  ]);
  const [overviewData, appEventsData, redeemedData] = await Promise.all([overviewRes.json(), appEventsRes.json(), redeemedRes.json()]);
  if (!overviewData.success) return null;
  const overview = overviewData.data?.overview || {};
  const stats = {
    totalPlaces: overview.totalPlaces ?? 0,
    totalUsers: overview.totalUsers ?? 0,
    totalCollections: overview.totalCollections ?? 0,
    totalBanners: overview.totalBanners ?? 0,
  };
  const appEvents = appEventsData.success ? appEventsData.data : null;
  const redeemedOffers = redeemedData.success ? redeemedData.data : null;
  const analytics = {
    ...overview,
    appEvents: appEvents?.events || [],
    appEventsConfigured: appEvents?.configured ?? overviewData.data?.appEventsConfigured ?? false,
    appEventsError: appEvents?.error || null,
    redeemedOffers,
  };
  return { stats, analytics };
}

export async function fetchRedemptions(headers, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') query.set(k, String(v));
  });
  const qs = query.toString();
  const res = await apiFetch(apiUrl(`/redemptions/admin${qs ? `?${qs}` : ''}`), { headers });
  const data = await res.json();
  return data.success ? (data.data || { items: [], total: 0, limit: 50, offset: 0 }) : null;
}

export async function fetchRedemptionDeletionHistory(headers, params = {}) {
  const query = new URLSearchParams();
  if (params.ambassadorCode != null && String(params.ambassadorCode).trim() !== '') {
    query.set('ambassadorCode', String(params.ambassadorCode).trim());
  }
  const qs = query.toString();
  const res = await apiFetch(apiUrl(`/redemptions/admin/deletions${qs ? `?${qs}` : ''}`), { headers });
  const data = await res.json();
  return data.success ? (data.data?.items || []) : null;
}

export async function deleteRedemption(headers, redemptionId, reason) {
  const res = await apiFetch(apiUrl(`/redemptions/admin/${redemptionId}`), {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ reason }),
  });
  return await res.json();
}

export async function fetchPlaceRedemptionStats(headers, placeId) {
  const res = await apiFetch(apiUrl(`/redemptions/place/${placeId}/stats`), { headers });
  const data = await res.json();
  return data.success ? (data.data || null) : null;
}

export async function fetchAmbassadors(headers, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') query.set(k, String(v));
  });
  const qs = query.toString();
  const res = await apiFetch(apiUrl(`/ambassadors/admin${qs ? `?${qs}` : ''}`), { headers });
  const data = await res.json();
  return data.success ? (data.data?.ambassadors || []) : null;
}

export async function createAmbassador(headers, body) {
  const res = await apiFetch(apiUrl('/ambassadors/admin'), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  return await res.json();
}

export async function updateAmbassador(headers, id, body) {
  const res = await apiFetch(apiUrl(`/ambassadors/admin/${id}`), {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });
  return await res.json();
}

export async function deleteAmbassador(headers, id) {
  const res = await apiFetch(apiUrl(`/ambassadors/admin/${id}`), {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ reason: 'Removed by admin from dashboard' }),
  });
  return await res.json();
}

export async function deleteAmbassadorWithReason(headers, id, reason) {
  const res = await apiFetch(apiUrl(`/ambassadors/admin/${id}`), {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ reason }),
  });
  return await res.json();
}

export async function fetchAmbassadorRedemptions(headers, ambassadorId, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') query.set(k, String(v));
  });
  const qs = query.toString();
  const res = await apiFetch(apiUrl(`/ambassadors/admin/${ambassadorId}/redemptions${qs ? `?${qs}` : ''}`), { headers });
  const data = await res.json();
  return data.success ? (data.data?.items || []) : null;
}

export async function fetchAmbassadorDeletionHistory(headers) {
  const res = await apiFetch(apiUrl('/ambassadors/admin/deletions'), { headers });
  const data = await res.json();
  return data.success ? (data.data?.items || []) : null;
}

export async function fetchReports(headers, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') query.set(k, String(v));
  });
  const qs = query.toString();
  const res = await apiFetch(apiUrl(`/reports${qs ? `?${qs}` : ''}`), { headers });
  const data = await res.json();
  return data.success ? (data.data || { items: [], total: 0, open: 0 }) : null;
}

export async function updateReportStatus(headers, reportId, status) {
  const res = await apiFetch(apiUrl(`/reports/${reportId}`), {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ status }),
  });
  return await res.json();
}
